import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Skeleton,
  IconButton,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import ProductList from '../components/home/ProductList';
import Pagination from '../components/main/Pagination';
import { getCollectionByUrl, getPaginatedProductsByCollection, Collection, CollectionProduct } from '../api/collections';
import type { Product } from '../types/product';

// Convert CollectionProduct to Product type for compatibility
const convertCollectionProductToProduct = (collectionProduct: CollectionProduct): Product => {
  return {
    id: collectionProduct.id,
    name: collectionProduct.name,
    nameZh: collectionProduct.nameZh || null,
    nameCn: collectionProduct.nameCn || null,
    description: collectionProduct.description,
    descriptionZh: collectionProduct.descriptionZh || null,
    descriptionCn: collectionProduct.descriptionCn || null,
    sku: collectionProduct.sku,
    barcode: collectionProduct.barcode || null,
    price: collectionProduct.price,
    comparePrice: collectionProduct.comparePrice || null,
    costPerItem: collectionProduct.costPerItem || null,
    trackQuantity: collectionProduct.trackQuantity,
    chargeTax: collectionProduct.chargeTax,
    continueSelling: collectionProduct.continueSelling,
    type: collectionProduct.type || null,
    brandId: collectionProduct.brandId,
    collections: collectionProduct.collections,
    tags: collectionProduct.tags || null,
    imageUrl: collectionProduct.imageUrl,
    imageGallery: collectionProduct.imageGallery,
    variantOptions: [],
    variants: [],
    isPhysicalProduct: collectionProduct.isPhysicalProduct,
    weight: collectionProduct.weight || null,
    weightUnit: collectionProduct.weightUnit,
    status: collectionProduct.isActive ? 'active' : 'inactive',
    onlineStore: true,
    pointOfSale: true,
    ourPick: collectionProduct.ourPick || false,
    eat365ProductId: collectionProduct.eat365ProductId || null,
    createdAt: collectionProduct.createdAt,
    vendor: collectionProduct.vendor || collectionProduct.brand,
    category: collectionProduct.category,
    brand: collectionProduct.brand,
    categories: collectionProduct.categories,
    inventory: {
      available: collectionProduct.stockAvailable || 0,
      inStock: collectionProduct.inStock || false,
      locations: [],
    },
    inStock: collectionProduct.inStock || false,
    quantity: collectionProduct.quantity || 0,
  };
};

const CollectionPage: React.FC = () => {
  const { urlPath } = useParams<{ urlPath: string }>();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [collection, setCollection] = useState<Collection | null>(null);
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const limit = isMobile ? 12 : 20; // Responsive limit: 12 for mobile, 20 for PC

  useEffect(() => {
    const fetchCollectionData = async () => {
      if (!urlPath) {
        setError('Invalid collection URL');
        setLoading(false);
        return;
      }
      
      try {
        setLoading(true);
        setError(null);
        
        // Fetch collection data
        const collectionData = await getCollectionByUrl(urlPath);
        
        // Check if collection is published online
        if (!collectionData.isPublishedOnline) {
          throw new Error('Collection is not published');
        }
        
        setCollection(collectionData);
        
        // Fetch products for this collection with pagination
        const productsResponse = await getPaginatedProductsByCollection(collectionData.id, currentPage, limit);
        const convertedProducts = productsResponse.products.map(convertCollectionProductToProduct);
        setProducts(convertedProducts);
        setTotalPages(productsResponse.totalPages);
        setTotalItems(productsResponse.total);
        
      } catch (err) {
        console.error('Error fetching collection data:', err);
        
        let errorMessage = 'Collection not found';
        if (err instanceof Error) {
          if (err.message === 'Collection is not published') {
            errorMessage = 'This collection is not available';
          } else if (err.message.includes('Network Error') || err.message.includes('Failed to fetch')) {
            errorMessage = 'Network error. Please check your connection.';
          }
        }
        
        setError(errorMessage);
        // Redirect to 404 or home page after a delay
        setTimeout(() => navigate('/'), 3000);
      } finally {
        setLoading(false);
      }
    };

    fetchCollectionData();
  }, [urlPath, currentPage]);

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Update document title when collection data is loaded
  useEffect(() => {
    if (collection) {
      document.title = collection.searchEngineTitle || `${collection.name} - Hatsuhiken`;
    }
  }, [collection]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleBackClick = () => {
    navigate('/');
  };

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Container maxWidth="lg" sx={{ py: 4, flex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <Skeleton variant="circular" width={40} height={40} sx={{ mr: 2 }} />
            <Skeleton variant="text" width={200} height={32} />
          </Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: 2 }}>
            {Array.from({ length: 8 }).map((_, index) => (
              <Skeleton key={index} variant="rectangular" width="100%" height={300} />
            ))}
          </Box>
        </Container>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Container maxWidth="lg" sx={{ textAlign: 'center' }}>
          <Typography variant="h5" color="error" sx={{ mb: 2 }}>
            {error}
          </Typography>
          <Typography color="text.secondary">
            Redirecting to home page...
          </Typography>
        </Container>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        pb: 4,
      }}
    >
      {/* Header */}
      <Box
        sx={{
          backgroundColor: 'white',
          borderBottom: '1px solid #E7E3D5',
          position: 'sticky',
          top: 0,
          zIndex: 1000,
        }}
      >
        <Container maxWidth="lg">
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              py: 2,
            }}
          >
            <IconButton
              onClick={handleBackClick}
              sx={{
                color: theme.palette.custom.brown,
                mr: 2,
              }}
            >
              <ArrowBackIcon />
            </IconButton>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                color: theme.palette.custom.brown,
              }}
            >
              {collection?.name || 'Collection'}
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Products Count */}
      <Container maxWidth="lg">
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 2,
            mt: 2,
          }}
        >
          <Typography color="text.secondary">
            {totalItems} items
          </Typography>
        </Box>
      </Container>

      {/* Products Section */}
      <Container sx={{ maxWidth: 'lg', margin: '0 auto', p: { xs: 2, md: 3 } }}>
        {products.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography variant="h6" sx={{ mb: 2, color: 'text.secondary' }}>
              No products found in this collection
            </Typography>
            <Typography color="text.secondary">
              Check back later for new products
            </Typography>
          </Box>
        ) : (
          <ProductList
            products={products}
            loading={false}
            variant="grid"
            title=""
          />
        )}
      </Container>

      {/* Pagination */}
      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={handlePageChange}
      />
    </Box>
  );
};

export default CollectionPage;
